import React, { useState, useEffect, useCallback } from 'react';
import { invoiceService, InvoiceItem, InvoiceDetails } from '../lib/invoiceService';
import { useAuth } from '../contexts/AuthContext';

interface InvoiceHistoryProps {
  onClose: () => void;
}

export const InvoiceHistory: React.FC<InvoiceHistoryProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInvoiceHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Loading invoice history for user:', user!.id);
      const history = await invoiceService.getInvoiceHistory(user!.id);
      console.log('📊 Invoice history loaded:', history);
      setInvoices(history);
    } catch (err) {
      console.error('❌ Error loading invoice history:', err);
      setError('Failed to load invoice history');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      loadInvoiceHistory();
    }
  }, [user?.id, loadInvoiceHistory]);

  const viewInvoiceDetails = async (invoiceId: string) => {
    try {
      const details = await invoiceService.getInvoiceDetails(invoiceId, user!.id);
      setSelectedInvoice(details);
    } catch (err) {
      console.error('❌ Error loading invoice details:', err);
      setError('Failed to load invoice details');
    }
  };

  const downloadInvoice = (invoice: InvoiceItem) => {
    if (invoice.invoice_pdf_url && typeof invoice.invoice_pdf_url === 'string') {
      window.open(invoice.invoice_pdf_url, '_blank');
    } else {
      setError('PDF not available for this invoice');
    }
  };

  const viewInvoiceOnline = (invoice: InvoiceItem) => {
    if (invoice.hosted_invoice_url && typeof invoice.hosted_invoice_url === 'string') {
      window.open(invoice.hosted_invoice_url, '_blank');
    } else {
      setError('Online invoice not available');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Invoice History</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Invoice History</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadInvoiceHistory}
              disabled={loading}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                  Refreshing...
                </>
              ) : (
                'Refresh'
              )}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {!loading && invoices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-4">📄</div>
            <p>No invoice history found</p>
            <p className="text-sm mt-2">Invoices will appear here once payments are processed.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <div
                key={invoice.invoice_id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoiceService.getStatusColor(invoice.status)}`}>
                        {invoiceService.getStatusText(invoice.status)}
                      </span>
                      {invoice.invoice_number && (
                        <span className="text-sm text-gray-600">
                          #{invoice.invoice_number}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <span className="ml-2 font-medium">
                          {invoiceService.formatAmount(invoice.amount_cents)}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Date:</span>
                        <span className="ml-2">{invoiceService.formatDate(invoice.created_at)}</span>
                      </div>
                    </div>

                    {invoice.description && (
                      <div className="mt-2 text-sm text-gray-600">
                        {invoice.description}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => viewInvoiceDetails(invoice.invoice_id)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View Details
                    </button>
                    
                    {invoice.invoice_pdf_url && (
                      <button
                        onClick={() => downloadInvoice(invoice)}
                        className="text-xs text-green-600 hover:text-green-700 font-medium"
                      >
                        Download PDF
                      </button>
                    )}
                    
                    {invoice.hosted_invoice_url && (
                      <button
                        onClick={() => viewInvoiceOnline(invoice)}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        View Online
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Invoice Details Modal */}
        {selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Invoice Details</h3>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Invoice Number:</span>
                    <span className="ml-2 font-medium">
                      {selectedInvoice.invoice_data.invoice_number || 'N/A'}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${invoiceService.getStatusColor(selectedInvoice.invoice_data.status)}`}>
                      {invoiceService.getStatusText(selectedInvoice.invoice_data.status)}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-gray-500">Amount:</span>
                    <span className="ml-2 font-medium">
                      {invoiceService.formatAmount(selectedInvoice.invoice_data.amount_cents)}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className="ml-2">
                      {invoiceService.formatDate(selectedInvoice.invoice_data.created_at)}
                    </span>
                  </div>
                </div>

                {selectedInvoice.invoice_data.description && (
                  <div>
                    <span className="text-gray-500">Description:</span>
                    <span className="ml-2">{selectedInvoice.invoice_data.description}</span>
                  </div>
                )}

                {selectedInvoice.line_items.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Line Items</h4>
                    <div className="space-y-2">
                      {selectedInvoice.line_items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div>
                            <span className="font-medium">{item.description}</span>
                            <span className="text-sm text-gray-500 ml-2">x{item.quantity}</span>
                          </div>
                          <span className="font-medium">
                            {invoiceService.formatAmount(item.total_amount_cents)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t">
                  {selectedInvoice.invoice_data.invoice_pdf_url && (
                    <button
                      onClick={() => {
                        if (selectedInvoice.invoice_data.invoice_pdf_url) {
                          window.open(selectedInvoice.invoice_data.invoice_pdf_url, '_blank');
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      Download PDF
                    </button>
                  )}
                  
                  {selectedInvoice.invoice_data.hosted_invoice_url && (
                    <button
                      onClick={() => {
                        if (selectedInvoice.invoice_data.hosted_invoice_url) {
                          window.open(selectedInvoice.invoice_data.hosted_invoice_url, '_blank');
                        }
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                    >
                      View Online
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            <p>• Invoices are automatically generated for successful payments</p>
            <p>• PDF invoices can be downloaded for your records</p>
            <p>• Contact support if you need invoice adjustments</p>
          </div>
        </div>
      </div>
    </div>
  );
}; 